import { supabase } from "@/integrations/supabase/client";
import type { Ad } from "@/lib/data";
import { categoryImages } from "@/components/category-icon";

export const MAX_PHOTOS = 15;
const SIGNED_URL_TTL = 60 * 60 * 24 * 365 * 5; // 5 il
export const PHOTO_BUCKET = "listing-photos";

export type ListingRow = {
  id: string;
  user_id: string;
  title: string;
  description: string;
  price: number;
  negotiable: boolean;
  category: string;
  city: string;
  phone?: string | null;
  whatsapp?: string | null;
  images: string[];
  badge: string;
  status: string;
  views: number;
  created_at: string;
  badge_expires_at?: string | null;
  badge_price?: number | null;
  specs?: Record<string, string> | null;
  auto_renew?: boolean | null;
  last_bumped_at?: string | null;
};

export const PLACEHOLDER_IMAGE = "/listing-placeholder.webp";

/** Şəkil yüklənməyibsə kateqoriyaya uyğun avtomatik şəkil. */
export const fallbackImage = (category: string) =>
  categoryImages[category] ?? PLACEHOLDER_IMAGE;

export const statusLabels: Record<string, string> = {
  draft: "Qaralama",
  pending: "Gözləyir",
  active: "Aktiv",
  rejected: "İmtina",
  sold: "Satılıb",
};

const VIDEO_RE = /\.(mp4|webm|mov|m4v)(\?|$)/i;

export const isVideoUrl = (url: string) => VIDEO_RE.test(url);

export function rowToAd(
  row: ListingRow,
  sellerName = "Albazar istifadəçisi",
  options: { isStore?: boolean } = {},
): Ad {
  const expiresAt = row.badge_expires_at ?? null;
  const expired = Boolean(expiresAt && new Date(expiresAt).getTime() <= Date.now());
  const badge = (expired ? "none" : (row.badge as Ad["badge"])) ?? "none";
  const media = row.images ?? [];
  const photos = media.filter((m) => !isVideoUrl(m));
  const video = media.find((m) => isVideoUrl(m));
  return {
    id: row.id,
    title: row.title,
    description: row.description,
    price: Number(row.price),
    negotiable: row.negotiable,
    category: row.category,
    city: row.city,
    phone: row.phone ?? "",
    whatsapp: (row.whatsapp ?? "").replace(/\D/g, ""),
    seller: sellerName,
    sellerSince: new Date(row.created_at).getFullYear().toString(),
    createdAt: row.created_at,
    views: row.views,
    badge,
    featured: badge !== "none",
    ownerId: row.user_id,
    badgePrice: expired ? 0 : Number(row.badge_price ?? 0),
    badgeExpiresAt: expired ? null : expiresAt,
    images: photos.length ? photos : [fallbackImage(row.category)],
    isStore: (options.isStore ?? false) || (row.specs ?? {})["_admin_store"] === "1",
    adminVip: (row.specs ?? {})["_admin_vip"] === "1",
    adminPremium: (row.specs ?? {})["_admin_premium"] === "1",
    ...(video ? { videoUrl: video } : {}),

    specs: (row.specs ?? {}) as Record<string, string>,
    status: (row.status === "active"
      ? "active"
      : row.status === "rejected"
        ? "rejected"
        : row.status === "sold"
          ? "sold"
          : "pending") as Ad["status"],
  };
}

const BASE_COLUMNS =
  "id, user_id, title, description, price, negotiable, category, city, images, badge, status, views, created_at, badge_expires_at, badge_price, specs, auto_renew, last_bumped_at";
const CONTACT_COLUMNS = "phone, whatsapp";

/** Əlaqə nömrələri bazada yalnız daxil olmuş istifadəçilərə açıqdır. */
async function selectColumns() {
  const { data } = await supabase.auth.getSession();
  return data.session ? `${BASE_COLUMNS}, ${CONTACT_COLUMNS}` : BASE_COLUMNS;
}

/** Paketin bitməsinə qalan tam gün sayı (bitibsə null). */
export function daysLeft(iso?: string | null): number | null {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return null;
  return Math.max(1, Math.ceil(diff / 86_400_000));
}

export async function fetchActiveListingsLimited(limit = 200): Promise<Ad[]> {
  const { data, error } = await supabase
    .from("listings")
    .select(await selectColumns())
    .in("status", ["active", "sold"])
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []).map((r) => rowToAd(r as unknown as ListingRow));
}

export const fetchActiveListings = (): Promise<Ad[]> => fetchActiveListingsLimited(200);

export async function fetchListingById(id: string): Promise<Ad | null> {
  const { data, error } = await supabase
    .from("listings")
    .select(await selectColumns())
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  return data ? rowToAd(data as unknown as ListingRow) : null;
}

export async function fetchMyListings(userId: string): Promise<ListingRow[]> {
  const { data, error } = await supabase
    .from("listings")
    .select(await selectColumns())
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as ListingRow[];
}

export async function uploadListingPhoto(userId: string, file: File): Promise<string> {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "jpg";
  const path = `${userId}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from(PHOTO_BUCKET).upload(path, file, {
    cacheControl: "31536000",
    upsert: false,
  });
  if (error) throw error;
  const { data, error: signError } = await supabase.storage
    .from(PHOTO_BUCKET)
    .createSignedUrl(path, SIGNED_URL_TTL);
  if (signError || !data) throw signError ?? new Error("Şəkil linki yaradıla bilmədi");
  return data.signedUrl;
}

export function isUuid(value: string) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

/** Elanı yuxarı çəkir (bump). */
export async function bumpListing(id: string) {
  const { error } = await supabase
    .from("listings")
    .update({ last_bumped_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

/** Avtomatik gündəlik irəli çəkməni aktiv/deaktiv edir. */
export async function setAutoRenew(id: string, autoRenew: boolean) {
  const { error } = await supabase.from("listings").update({ auto_renew: autoRenew }).eq("id", id);
  if (error) throw error;
}

/** Elan sahibi məhsulun satıldığını bildirir (elan silinmir). */
export async function markListingSold(id: string) {
  const { error } = await supabase.from("listings").update({ status: "sold" }).eq("id", id);
  if (error) throw error;
}

export async function incrementViews(id: string) {
  if (!isUuid(id)) return;
  const { bumpListingViews } = await import("@/lib/views.functions");
  await bumpListingViews({ data: { id } }).catch(() => {});
}
export async function uploadAvatar(userId: string, file: File): Promise<string> {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "jpg";
  const path = `${userId}/avatar-${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from(PHOTO_BUCKET).upload(path, file, {
    cacheControl: "31536000",
    upsert: false,
  });
  if (error) throw error;
  const { data, error: signError } = await supabase.storage
    .from(PHOTO_BUCKET)
    .createSignedUrl(path, SIGNED_URL_TTL);
  if (signError || !data) throw signError ?? new Error("Şəkil linki yaradıla bilmədi");
  return data.signedUrl;
}

/** Elana əlavə olunan mağaza videosu (yalnız mağaza sahibləri üçün). */
export const MAX_LISTING_VIDEO_MB = 100;
export const MAX_LISTING_VIDEO_SECONDS = 15;
export const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/webm", "video/quicktime", "video/x-m4v"];
const LISTING_VIDEO_BUCKET = "store-videos";

/** Brauzerdə video uzunluğunu ölçür (mümkün olmasa 0 qaytarır). */
export function readVideoDuration(file: File): Promise<number> {
  return new Promise((resolve) => {
    if (typeof document === "undefined") return resolve(0);
    const url = URL.createObjectURL(file);
    const el = document.createElement("video");
    el.preload = "metadata";
    const done = (d: number) => {
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(d) ? d : 0);
    };
    el.onloadedmetadata = () => done(el.duration);
    el.onerror = () => done(0);
    el.src = url;
  });
}

export async function uploadListingVideo(userId: string, file: File): Promise<string> {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "mp4";
  if (!/^(mp4|webm|mov|m4v)$/.test(ext)) {
    throw new Error("Yalnız MP4, WEBM, MOV formatlı video yükləmək olar");
  }
  if (file.type && !ALLOWED_VIDEO_TYPES.includes(file.type) && !file.type.startsWith("video/")) {
    throw new Error("Seçilmiş fayl video deyil");
  }
  if (file.size > MAX_LISTING_VIDEO_MB * 1024 * 1024) {
    throw new Error(`Video ${MAX_LISTING_VIDEO_MB} MB-dan böyük ola bilməz`);
  }
  const duration = await readVideoDuration(file);
  if (duration > MAX_LISTING_VIDEO_SECONDS + 0.5) {
    throw new Error(`Video ən çox ${MAX_LISTING_VIDEO_SECONDS} saniyə ola bilər`);
  }

  const path = `${userId}/listing-${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from(LISTING_VIDEO_BUCKET).upload(path, file, {
    cacheControl: "31536000",
    upsert: false,
    contentType: file.type || "video/mp4",
  });
  if (error) throw error;
  const { data, error: signError } = await supabase.storage
    .from(LISTING_VIDEO_BUCKET)
    .createSignedUrl(path, SIGNED_URL_TTL);
  if (signError || !data) throw signError ?? new Error("Video linki yaradıla bilmədi");
  return data.signedUrl;
}
